import { DmsModal, DmsButton, intl } from 'dmsbasereact';
import { Box, Grid, Typography, Paper, Chip, CircularProgress } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { getGenderEnum } from '../../constants/GenderEnum';
import { normalizeGender, formatCpf } from '../../utils/formatters';

/**
 * Modal for previewing prescription data before creation
 * @param {Object} props - Component properties
 * @param {boolean} props.isVisible - Controls modal visibility
 * @param {function} props.onClose - Callback to close modal
 * @param {function} props.onConfirm - Callback to confirm and create prescription
 * @param {Object} props.prescriptionData - Prescription data to preview
 * @param {Object} props.patientData - Patient data to display
 * @param {Object} props.doctorInfo - Doctor information
 * @param {Array} props.institutions - List of institutions
 * @param {boolean} props.allowAlternativeDispensation - Whether alternative dispensation is allowed
 * @param {Array} props.highlyEffectiveMethodOptions - List of highly effective contraceptive method options
 * @param {Array} props.barrierMethodOptions - List of barrier contraceptive method options
 * @param {boolean} props.isLoading - Whether the prescription is being created
 * @returns {JSX.Element} Prescription preview modal component
 */
const PrescriptionPreviewModal = ({
  isVisible,
  onClose,
  onConfirm,
  prescriptionData = null,
  patientData = null,
  doctorInfo = null,
  selectedCrm = null,
  institutions = [],
  allowAlternativeDispensation,
  highlyEffectiveMethodOptions = [],
  barrierMethodOptions = [],
  isLoading = false
}) => {
  if (!prescriptionData || !patientData) {
    return null;
  }

  const getInstitutionName = () => {
    const institution = institutions.find(
      inst => inst.organization_branch_pk === prescriptionData.organization_branch_pk
    );
    return institution?.organization_branch?.name || '-';
  };

  const getIndicationText = (indication) => {
    const indicationMap = {
      1: intl.get("MultipleMyeloma1stLineEligible").d("Multiple Myeloma 1st Line Eligible"),
      2: intl.get("MultipleMyelomaMaintenance").d("Multiple Myeloma Maintenance"),
      3: intl.get("MultipleMyeloma1stLineIneligible").d("Multiple Myeloma 1st Line Ineligible"),
      4: intl.get("MultipleMyelomaRelapsedRefractory").d("Multiple Myeloma Relapsed/Refractory"),
      5: intl.get("SMD5QDeletion").d("MDS 5Q Deletion"),
      6: intl.get("LymphomaRelapsedRefractory").d("Lymphoma Relapsed/Refractory"),
      7: intl.get("OtherIndication").d("Other")
    };
    return indicationMap[indication] || '-';
  };

  const getMethodLabels = (methodValues, methodOptions) => {
    if (!methodValues || methodValues.length === 0) return [];
    return methodValues.map(value => {
      const method = methodOptions.find(m => m.value === value);
      return method?.label || value;
    });
  };

  const SectionHeader = ({ icon: Icon, title, color = 'primary.main' }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Box sx={{
        p: 1,
        borderRadius: 1,
        bgcolor: `${color.split('.')[0]}.50`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Icon sx={{ color, fontSize: 24 }} />
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
        {title}
      </Typography>
    </Box>
  );

  const DataRow = ({ label, value, fullWidth = false }) => (
    <Grid item xs={12} sm={fullWidth ? 12 : 6}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {value || '-'}
      </Typography>
    </Grid>
  );

  const isFemaleWithPotential = normalizeGender(patientData.gender) === getGenderEnum().F.value && patientData.has_pregnancy_potential;

  return (
    <DmsModal isVisible={isVisible} onClose={onClose} maxWidth="md" fullWidth>
      <DmsModal.Header
        title={intl.get("PrescriptionPreview").d("Prescription Review")}
        onClose={onClose}
      />
      <DmsModal.Body>
        <Box sx={{ py: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {intl.get("PrescriptionPreviewTitle").d("Review all data before creating the prescription")}
          </Typography>

          {/* Patient Data Section */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.100', borderRadius: 2 }}>
            <SectionHeader icon={PersonIcon} title={intl.get("patient_data").d("Patient Data")} color="primary.main" />

            {/* Personal Data */}
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.dark', display: 'block', mb: 1 }}>
              {intl.get("PersonalData").d("Personal Data")}
            </Typography>
            <Grid container spacing={2}>
              <DataRow label={intl.get("name").d("Name")} value={patientData.name} fullWidth />
              <DataRow label={intl.get("CPF").d("CPF")} value={formatCpf(patientData.cpf)} />
              <DataRow
                label={intl.get("BirthDate").d("Birth Date")}
                value={patientData.birth_date ? dayjs(patientData.birth_date).format('DD/MM/YYYY') : '-'}
              />
              <DataRow
                label={intl.get("gender").d("Gender")}
                value={normalizeGender(patientData.gender) === getGenderEnum().M.value ? intl.get("male").d("Male") : intl.get("female").d("Female")}
              />
              {patientData.rg && (
                <DataRow label={intl.get("rg").d("RG")} value={patientData.rg} />
              )}
            </Grid>

            {/* Medical Information */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'primary.200' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.dark', display: 'block', mb: 1 }}>
                {intl.get("MedicalInfo").d("Medical Information")}
              </Typography>
              <Grid container spacing={2}>
                <DataRow
                  label={intl.get("PregnancyPotential").d("Pregnancy Potential")}
                  value={isFemaleWithPotential
                    ? intl.get("HasPregnancyPotential").d("Has Pregnancy Potential")
                    : intl.get("NoPregnancyPotentialShort").d("Sem potencial de gravidez")}
                />
                {isFemaleWithPotential && prescriptionData.pregnancy_test_date && (
                  <DataRow
                    label={intl.get("LastPregnancyTestDate").d("Last Pregnancy Test Date")}
                    value={dayjs(prescriptionData.pregnancy_test_date).format('DD/MM/YYYY')}
                  />
                )}
              </Grid>
            </Box>

            {/* Legal Guardian if exists */}
            {patientData.responsible_legal && patientData.responsible_legal.length > 0 && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'primary.200' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.dark', display: 'block', mb: 1 }}>
                  {intl.get("ResponsibleLegal").d("Legal Guardian")}
                </Typography>
                <Grid container spacing={2}>
                  <DataRow label={intl.get("name").d("Name")} value={patientData.responsible_legal[0].name} />
                  <DataRow label={intl.get("CPF").d("CPF")} value={formatCpf(patientData.responsible_legal[0].cpf)} />
                </Grid>
              </Box>
            )}
          </Paper>

          {/* Doctor Data Section */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.100', borderRadius: 2 }}>
            <SectionHeader icon={LocalHospitalIcon} title={intl.get("doctor_data").d("Doctor Data")} color="success.main" />
            <Grid container spacing={2}>
              <DataRow label={intl.get("prescribing_doctor").d("Prescribing Doctor")} value={doctorInfo?.doctor_name} fullWidth />
              <DataRow label={intl.get("CRM").d("CRM")} value={selectedCrm ? `${selectedCrm.crm}/${selectedCrm.crm_state}` : (doctorInfo?.crm ? `${doctorInfo.crm}/${doctorInfo.crm_state}` : '-')} />
              <DataRow label={intl.get("Institution").d("Institution")} value={getInstitutionName()} />
            </Grid>
          </Paper>

          {/* Prescription Data Section */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 2, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.100', borderRadius: 2 }}>
            <SectionHeader icon={DescriptionIcon} title={intl.get("PrescriptionDetails").d("Prescription Details")} color="info.main" />
            <Grid container spacing={2}>
              <DataRow
                label={intl.get("Posology").d("Posology")}
                value={prescriptionData.dosage_description}
                fullWidth
              />
              {prescriptionData.dosage_notes && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {intl.get("AdditionalNotes").d("Additional Notes")}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
                    {prescriptionData.dosage_notes}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {intl.get("Indication").d("Indication")}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {getIndicationText(prescriptionData.indication)}
                </Typography>
                {prescriptionData.indication === 7 && prescriptionData.indication_other && (
                  <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
                    {prescriptionData.indication_other}
                  </Typography>
                )}
              </Grid>
              <DataRow
                label={intl.get("prescription_date").d("Prescription Date")}
                value={dayjs(prescriptionData.prescription_date).format('DD/MM/YYYY')}
              />
              <DataRow
                label={intl.get("ExpirationDate").d("Expiration Date")}
                value={dayjs(prescriptionData.expiration_date).format('DD/MM/YYYY')}
              />
            </Grid>

            {/* Contraceptive Methods for female with pregnancy potential */}
            {isFemaleWithPotential && prescriptionData.highly_effective_methods && prescriptionData.barrier_methods && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'info.200' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'info.dark', display: 'block', mb: 1.5 }}>
                  {intl.get("ContraceptiveMethodsUsed").d("Contraceptive Methods in Use")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                      {intl.get("HighlyEffectiveMethods").d("Highly Effective Method")}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {getMethodLabels(prescriptionData.highly_effective_methods, highlyEffectiveMethodOptions).map((label, index) => (
                        <Chip key={index} label={label} size="small" color="success" variant="outlined" sx={{ justifyContent: 'flex-start' }} />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                      {intl.get("BarrierMethods").d("Barrier Method")}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {getMethodLabels(prescriptionData.barrier_methods, barrierMethodOptions).map((label, index) => (
                        <Chip key={index} label={label} size="small" color="warning" variant="outlined" sx={{ justifyContent: 'flex-start' }} />
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>

          {/* Confirmations Section */}
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
              {intl.get("Confirmations").d("Confirmations")}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                <Typography variant="body2">
                  {intl.get("CommitToSignDocuments").d("Liability Term: I commit to print and collect the signatures of the 3 generated documents.")}
                </Typography>
              </Box>
              {allowAlternativeDispensation && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                  <Typography variant="body2">
                    {intl.get("AllowAlternativeDispensation").d("I authorize the pharmacist to dispense a different presentation if necessary")}
                  </Typography>
                </Box>
              )}
              {isFemaleWithPotential && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                    <Typography variant="body2">
                      {intl.get("ConfirmNegativePregnancyTest").d("I confirm the test was performed with a negative result.")}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18, mt: 0.25 }} />
                    <Typography variant="body2">
                      {intl.get("MedicalRecordsConfirmation").d("Medical records have been archived with each prescription, including pregnancy exclusion proof, one copy of the prescription, and contraceptive methods guidance.")}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Paper>
        </Box>
      </DmsModal.Body>
      <DmsModal.Footer>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, width: '100%' }}>
          <DmsButton
            variant="secondary"
            text={intl.get("BackToEdit").d("Back to Edit")}
            onClick={onClose}
            disabled={isLoading}
          />
          <DmsButton
            variant="primary"
            text={isLoading
              ? intl.get("CreatingPrescription").d("Creating Prescription...")
              : intl.get("ConfirmAndCreate").d("Confirmar e Criar")
            }
            onClick={onConfirm}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : null}
          />
        </Box>
      </DmsModal.Footer>
    </DmsModal>
  );
};

PrescriptionPreviewModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  prescriptionData: PropTypes.object,
  patientData: PropTypes.object,
  doctorInfo: PropTypes.object,
  selectedCrm: PropTypes.object,
  institutions: PropTypes.array,
  allowAlternativeDispensation: PropTypes.bool,
  highlyEffectiveMethodOptions: PropTypes.array,
  barrierMethodOptions: PropTypes.array,
  isLoading: PropTypes.bool
};


export default PrescriptionPreviewModal;
